import * as React from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import clsx from 'clsx';
import { MicrophoneIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { formatDuration } from '@/utils/formatters';

interface RecordingInterfaceProps {
  recordingTime: number;
  audioData: number[]; // Array of 0-255 values
  onStop: () => void;
  onCancel: () => void;
}

// Pause Icon (Red bars)
const PauseIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='3' y='2' width='3' height='10' rx='1.5' fill='#F36568' />
    <rect x='8' y='2' width='3' height='10' rx='1.5' fill='#F36568' />
  </svg>
);

export const RecordingInterface: React.FC<RecordingInterfaceProps> = ({
  recordingTime,
  audioData,
  onStop,
  onCancel,
}) => {
  // Detect if voice input has been captured (sticky: once true, stays true)
  const [hasVoice, setHasVoice] = React.useState(false);
  React.useEffect(() => {
    if (hasVoice) return;
    const loud = audioData.filter((val) => val > 25).length;
    if (loud >= 3) setHasVoice(true);
  }, [audioData, hasVoice]);

  // Slide to Cancel Logic
  const dragX = useMotionValue(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  // Map drag position to opacity of the "Slide to cancel" text
  const textOpacity = useTransform(dragX, [0, -100], [1, 0]);
  const iconScale = useTransform(dragX, [0, -50], [1, 1.2]);

  const handleDragEnd = () => {
    const currentX = dragX.get();
    setIsDragging(false);
    // Threshold to trigger cancel (e.g., dragged 100px left)
    if (currentX < -80) {
      onCancel();
    } else {
      // Reset position directly to avoid controls.start() mount timing issue
      dragX.set(0);
    }
  };

  return (
    <div
      className={clsx(
        'relative flex w-full justify-between',
        // Mobile: Column layout, taller
        'h-auto min-h-[68px] flex-col py-2 md:py-0',
        // Desktop: Row layout, fixed height
        'md:h-[68px] md:flex-row md:items-center',
        'rounded-3xl px-2'
      )}
    >
      {/* Waveform Visualization - Top on mobile, Middle on Desktop */}
      {/* FIXED: Enforce h-[50px] on mobile to prevent jumping, shrink-0 to fix size. 30px on desktop. */}
      <div className='order-1 flex h-[50px] w-full shrink-0 items-center justify-center gap-[3px] overflow-hidden md:order-2 md:mx-4 md:h-[30px] md:flex-1'>
        {audioData.map((val, idx) => (
          <motion.div
            key={idx}
            className='w-[3px] rounded-full bg-white'
            animate={{
              height: `${Math.max(4, val / 2)}px`,
              opacity: 0.6 + (idx / audioData.length) * 0.4, // fade left->right
            }}
            transition={{ type: 'tween', duration: 0.1 }}
          />
        ))}
      </div>

      {/* Controls Container - Wraps Left and Right groups on mobile */}
      {/* Mobile: Order 2 (Bottom), Row with justify-between */}
      {/* Desktop: Contents (unwrap), relying on flex parent */}
      <div className='order-2 mt-2 flex w-full items-center justify-between md:mt-0 md:contents'>
        {/* Left Group: Stop Button + Timer */}
        <div className='z-20 flex items-center gap-4 pl-4 md:order-1'>
          <button
            onClick={onCancel}
            className='flex size-10 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-white/20'
          >
            <PauseIcon />
          </button>
          <span className='w-[40px] font-sans text-16 font-medium text-white'>
            {formatDuration(recordingTime)}
          </span>
        </div>

        {/* Right Group: Slide/Press to Cancel + Mic */}
        <div
          className='relative z-20 flex min-w-[160px] items-center justify-end md:order-3'
          ref={containerRef}
        >
          {/* --- MOBILE: Slide to Cancel Text --- */}
          <motion.div
            style={{ opacity: textOpacity }}
            className='pointer-events-none absolute right-[60px] block whitespace-nowrap pr-4 font-sans text-16 font-medium leading-160 tracking-[-0.02em] text-gray-450 md:hidden'
          >
            {'< Slide to cancel'}
          </motion.div>

          {/* --- DESKTOP: Press to Cancel Button --- */}
          <button
            type='button'
            onClick={onCancel}
            className='absolute right-[60px] hidden whitespace-nowrap pr-4 font-sans text-16 font-medium leading-160 tracking-[-0.02em] text-gray-450 hover:text-red-400 md:block'
          >
            Press to cancel
          </button>

          {/* --- Mic / Send Button (Draggable on Mobile, Static on Desktop) --- */}
          <div className='relative z-30'>
            {/* Mobile Draggable Wrapper */}
            <motion.div
              className='md:hidden'
              drag='x'
              dragConstraints={{ left: -200, right: 0 }}
              dragElastic={0.1}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={handleDragEnd}
              style={{ x: dragX, scale: iconScale }}
              onMouseUp={() => {
                if (!isDragging && dragX.get() === 0) {
                  onStop();
                }
              }}
            >
              {hasVoice ? (
                <div className='flex size-[44px] cursor-pointer items-center justify-center'>
                  <PaperAirplaneIcon className='size-6 text-accent-300' />
                </div>
              ) : (
                <div className='flex size-[44px] cursor-pointer items-center justify-center rounded-full bg-brand-600 shadow-lg active:cursor-grabbing'>
                  <MicrophoneIcon className='size-6 text-white' />
                </div>
              )}
            </motion.div>

            {/* Desktop Static Clickable Wrapper */}
            {hasVoice ? (
              <button
                type='button'
                onClick={onStop}
                className='hidden size-[44px] items-center justify-center transition-colors hover:text-accent-200 md:flex'
              >
                <PaperAirplaneIcon className='size-6 text-accent-300' />
              </button>
            ) : (
              <button
                type='button'
                onClick={onStop}
                className='hidden size-[44px] items-center justify-center rounded-full bg-brand-600 shadow-lg transition-transform hover:scale-105 active:scale-95 md:flex'
              >
                <MicrophoneIcon className='size-6 text-white' />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
